import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { arClass } from "../../../../utils/useArClass";
import Txt from "../../../Text";
import CustomSelect from "../../../CustomSelect";
import api from "../../../../api";
import { showAlert } from "../../../../redux/alert/alertActions";
import {
  ArrowLeft,
  Save,
  Loader,
  Users,
  CheckCircle,
  Lock,
  Flag,
  ChartPie,
  TestTubeDiagonal,
  Pill,
  Folder,
  Calendar,
  MessageCircle,
  Accessibility,
  Receipt,
  ShieldCheck,
  Info,
} from "lucide-react";
import textTranslator from "../../../../utils/useTextTranslator";
import { useUser } from "../../../../authentication/authentication";
import RoleBadge from "../../../RoleListPreview";
import RoleListPreview from "../../../RoleListPreview";

const Text = ({ children, className, page = "MY_REQUESTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const priorityOptions = {
  LOW: {
    label: {
      en: "Low",
      ar: "منخفض",
      fr: "Faible",
    },
    value: "LOW",
  },
  MEDIUM: {
    label: {
      en: "Medium",
      ar: "متوسط",
      fr: "Moyen",
    },
    value: "MEDIUM",
  },
  HIGH: {
    label: {
      en: "High",
      ar: "مرتفع",
      fr: "Élevé",
    },
    value: "HIGH",
  },
  CRITICAL: {
    label: {
      en: "Critical",
      ar: "حرج",
      fr: "Critique",
    },
    value: "CRITICAL",
  },
};

const accessRequestTypeOptions = {
  ACCESS_ISSUE: {
    label: {
      en: "Access Issue",
      ar: "مشكلة وصول",
      fr: "Problème d'accès",
    },
    value: "ACCESS_ISSUE",
  },
  DEACTIVATION: {
    label: {
      en: "Deactivation",
      ar: "إلغاء التفعيل",
      fr: "Désactivation",
    },
    value: "DEACTIVATION",
  },
  DELETION: {
    label: {
      en: "Deletion",
      ar: "الحذف",
      fr: "Suppression",
    },
    value: "DELETION",
  },
  REACTIVATION: {
    label: {
      en: "Reactivation",
      ar: "إعادة التفعيل",
      fr: "Réactivation",
    },
    value: "REACTIVATION",
  },
};

const CreateUserAccessRequest = ({ onCancel }) => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const localUser = useUser();

  const systemOptions = [
    {
      value: "Patients Management",
      name: textTranslator("Patients Management", "MY_REQUESTS"),
      icon: Accessibility,
    },
    { value: "Payments System", name: textTranslator("Payments System", "MY_REQUESTS"), icon: Receipt },
    {
      value: "Visits Tracking",
      name: textTranslator("Visits Tracking", "MY_REQUESTS"),
      icon: MessageCircle,
    },
    { value: "Appointments", name: textTranslator("Appointments", "MY_REQUESTS"), icon: Calendar },
    { value: "Reports", name: textTranslator("Reports", "MY_REQUESTS"), icon: Folder },
    { value: "Medications", name: textTranslator("Medications", "MY_REQUESTS"), icon: Pill },
    {
      value: "Lab Tests",
      name: textTranslator("Lab Tests", "MY_REQUESTS"),
      icon: TestTubeDiagonal,
    },
    { value: "Statistics", name: textTranslator("Statistics", "MY_REQUESTS"), icon: ChartPie },
  ];

  useEffect(() => {
    // Fetch users for selection
    const fetchUsers = async () => {
      try {
        setFetching(true);
        const response = await api.get("users/team/?page=1&limit=50");
        setUsers(response.data.results || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        dispatch(
          showAlert({
            type: "error",
            title: "Error",
            message: "Failed to load users",
          }),
        );
      } finally {
        setFetching(false);
      }
    };
    fetchUsers();
  }, [dispatch]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "MEDIUM",
    accessRequestType: "ACCESS_ISSUE",
    accessIssueDescription: "",
    systemsAffected: [],
    requestedAction: "",
    permanentDeletion: false,
    dataHandling: "",
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSystemToggle = (system) => {
    const englishSystemName =
      systemOptions.find((s) => s.value === system)?.value || system;
    setFormData((prev) => ({
      ...prev,
      systemsAffected: prev.systemsAffected.includes(englishSystemName)
        ? prev.systemsAffected.filter((s) => s !== englishSystemName)
        : [...prev.systemsAffected, englishSystemName],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: "Please select a target user",
        }),
      );
      return;
    }

    setLoading(true);

    try {
      await api.post("it-requests", {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        requestType: "USER_ACCESS_REQUEST",
        targetUserId: selectedUser.id,
        assignedToId: selectedUser.id,
        userAccessDetails: {
          accessRequestType: formData.accessRequestType,
          accessIssueDescription: formData.accessIssueDescription,
          systemsAffected: formData.systemsAffected,
          requestedAction: formData.requestedAction,
          permanentDeletion: formData.permanentDeletion,
          dataHandling: formData.dataHandling,
        },
      });

      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Success", "MY_REQUESTS"),
          message: textTranslator(
            "User access request created successfully",
            "MY_REQUESTS",
          ),
        }),
      );

      navigate("/my-requests");
    } catch (error) {
      console.error("Error creating request:", error);
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: error?.response?.data?.message || "Failed to create request",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={arClass(
        `min-h-screen ${
          darkMode
            ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
            : "bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50"
        } transition-colors duration-300 py-8`,
      )}
    >
      <div className={arClass("mx-auto px-4 sm:px-6 lg:px-8")}>
        {/* Back Button */}
        <motion.button
          onClick={onCancel}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={arClass(
            `flex items-center gap-2 px-4 py-2 rounded-lg font-medium mb-8 transition-all ${
              darkMode
                ? "bg-gray-800/50 hover:bg-gray-700/80 text-gray-300 hover:text-white border border-gray-700"
                : "bg-white/50 hover:bg-white text-gray-600 hover:text-gray-900 border border-gray-200 shadow-sm"
            }`,
          )}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft className="w-4 h-4" />
          <Text>Back</Text>
        </motion.button>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={arClass(
            `rounded-2xl overflow-hidden shadow-2xl border ${
              darkMode
                ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700/50"
                : "bg-white border-gray-200/50"
            }`,
          )}
        >
          {/* Header Section */}
          <div
            className={arClass(
              `bg-gradient-to-r from-blue-600 to-cyan-600 px-2 py-4 sm:p-6 relative overflow-hidden`,
            )}
          >
            <motion.div
              className="absolute inset-0 opacity-10"
              animate={{
                background: [
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 80% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                ],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <div className={arClass("relative z-10")}>
              <div className={arClass("flex items-center gap-3 mb-2")}>
                <Lock className="text-white w-10 h-10 max-sm:w-6 max-sm:h-6" />
                <h1 className="text-3xl max-sm:text-xl font-bold text-white">
                  <Text>User Access Request</Text>
                </h1>
              </div>
              <p className={arClass("text-blue-100 text-sm")}>
                <Text>Request access changes for your team members</Text>
              </p>
            </div>
          </div>

          {/* Content */}
          <div className={arClass(`px-2 py-4 sm:p-6`)}>
            {fetching ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={arClass(
                  "flex flex-col items-center justify-center py-16",
                )}
              >
                <Loader
                  className={`text-5xl animate-spin mb-4 w-12 h-12 ${darkMode ? "text-blue-400" : "text-blue-600"}`}
                />
                <p
                  className={arClass(
                    `${darkMode ? "text-gray-400" : "text-gray-600"}`,
                  )}
                >
                  <Text>Loading team members</Text>...
                </p>
              </motion.div>
            ) : (
              <>
                {/* Users Banner */}
                {users.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className={arClass(
                      `mb-8 p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border-blue-600/50"
                          : "bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200"
                      }`,
                    )}
                  >
                    <div className={arClass("flex items-center gap-3")}>
                      <Users
                        className={arClass(
                          "text-2xl flex-shrink-0 " +
                            (darkMode ? "text-blue-400" : "text-blue-600"),
                        )}
                      />
                      <div>
                        <p
                          className={arClass(
                            `text-sm font-medium ${darkMode ? "text-blue-200" : "text-blue-700"}`,
                          )}
                        >
                          <Text>Step 1: Select Team Member</Text>
                        </p>
                        <p
                          className={arClass(
                            `text-sm ${darkMode ? "text-blue-100/70" : "text-blue-900/70"}`,
                          )}
                        >
                          <Text>
                            Choose a team member to request user access changes
                            for
                          </Text>
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Form */}
                <form
                  onSubmit={handleSubmit}
                  className={arClass("normal-form space-y-6")}
                >
                  {/* Section 1: Select User */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={arClass(
                      `p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gray-800/50 border-gray-700/50"
                          : "bg-gray-50/50 border-gray-200"
                      }`,
                    )}
                  >
                    <div className={arClass("flex items-center gap-2 mb-6")}>
                      <Users
                        className={arClass(
                          "text-lg " +
                            (darkMode ? "text-blue-400" : "text-blue-600"),
                        )}
                      />
                      <h2
                        className={arClass(
                          `text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`,
                        )}
                      >
                        <Text>Team Members</Text>
                      </h2>
                    </div>

                    <div
                      className={arClass(
                        "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4",
                      )}
                    >
                      {users.map((member) =>
                          <motion.button
                            key={member.id}
                            type="button"
                            onClick={() => setSelectedUser(member)}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={arClass(
                              `p-4 rounded-lg border transition-all flex flex-col items-start relative ${
                                selectedUser?.id === member.id
                                  ? darkMode
                                    ? "border-blue-500 bg-blue-900/20"
                                    : "border-blue-500 bg-blue-50"
                                  : darkMode
                                    ? "border-gray-600 hover:border-gray-500"
                                    : "border-gray-200 hover:border-gray-300"
                              }`,
                            )}
                          >
                            {String(localUser.id) === String(member.id) && (
                            <div
                              className={arClass(
                                "absolute top-2 right-2 p-1.5 bg-green-500/20 border border-green-500 z-[1000] font-medium rounded-md flex items-center gap-1 duration-200 transition-all",
                              )}
                            >
                              <ShieldCheck className="w-3 h-3 text-green-500" />
                              <span className={arClass("text-green-600 text-xs font-semibold")}>
                                <Text>This is you</Text>
                              </span>
                            </div>
                          )}
                            <div
                              className={arClass(
                                "flex items-center gap-3 mb-2",
                              )}
                            >
                              <img
                                src={member.profileImage}
                                alt={member.firstName}
                                className={arClass(
                                  "w-10 h-10 rounded-full object-cover",
                                )}
                              />
                              <div
                                className={arClass(
                                  "flex-1 flex flex-col items-start min-w-0",
                                )}
                              >
                                <p
                                  className={arClass(
                                    `font-semibold text-sm truncate ${darkMode ? "text-white" : "text-gray-900"}`,
                                  )}
                                >
                                  {member.firstName} {member.lastName}
                                </p>
                                <RoleListPreview
                                  paddingY="py-1"
                                  my="my-0"
                                  style="bg-text"
                                  roles={(member.roles || []).map(
                                    (role) => role?.name || role,
                                  )}
                                />
                               
                              </div>
                            </div>
                            {selectedUser?.id === member.id && (
                              <div
                                className={arClass(
                                  "flex items-center gap-1 text-blue-600 text-xs mt-2",
                                )}
                              >
                                <CheckCircle className={arClass("w-4 h-4")} />
                                <span>
                                  <Text>Selected</Text>
                                </span>
                              </div>
                            )}
                          </motion.button>
                        
                      )}
                    </div>
                  </motion.div>
                  {/* Section 2: Request Details */}
                  {selectedUser && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      className={arClass(
                        `p-6 rounded-xl border-2 ${
                          darkMode
                            ? "bg-gray-800/50 border-gray-700/50"
                            : "bg-gray-50/50 border-gray-200"
                        }`,
                      )}
                    >
                      <div className={arClass("flex items-center gap-2 mb-6")}>
                        <Flag
                          className={arClass(
                            "text-lg " +
                              (darkMode ? "text-cyan-400" : "text-cyan-600"),
                          )}
                        />
                        <h2
                          className={arClass(
                            `text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`,
                          )}
                        >
                          <Text>Access Request Details</Text>
                        </h2>
                      </div>

                      <div className={arClass("space-y-4")}>
                        {/* Title */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <label
                            className={arClass(
                              `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                            )}
                          >
                            <Text>Title</Text>{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder={textTranslator(
                              "titlePlaceholder",
                              "MY_REQUESTS",
                            )}
                            required
                            className={arClass(
                              `w-full mt-4 px-4 py-3 rounded-lg border transition-all ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                              }`,
                            )}
                          />
                        </motion.div>

                        {/* Description */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.35 }}
                        >
                          <label
                            className={arClass(
                              `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                            )}
                          >
                            <Text>Description</Text>{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder={textTranslator(
                              "descriptionPlaceholder",
                              "MY_REQUESTS",
                            )}
                            required
                            rows={4}
                            className={arClass(
                              `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                              }`,
                            )}
                          />
                        </motion.div>

                        {/* Priority and Request Type */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 }}
                          >
                            <label
                              className={arClass(
                                `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                              )}
                            >
                              <Text>Priority</Text>
                            </label>
                            <CustomSelect
                              options={Object.entries(priorityOptions).map(
                                ([key, value]) => ({
                                  value: key,
                                  label: value.label[defaultLanguage] || key,
                                }),
                              )}
                              value={formData.priority || ""}
                              name="priority"
                              onChange={handleInputChange}
                              placeholder={textTranslator(
                                "priorityPlaceholder",
                                "MY_REQUESTS",
                              )}
                              allowCreate={false}
                              allowSearch={false}
                              className="max-sm:text-sm mt-4"
                            />
                          </motion.div>

                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.45 }}
                          >
                            <label
                              className={arClass(
                                `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                              )}
                            >
                              <Text>Access Request Type</Text>
                            </label>
                            <CustomSelect
                              options={Object.entries(
                                accessRequestTypeOptions,
                              ).map(([key, value]) => ({
                                value: key,
                                label: value.label[defaultLanguage] || key,
                              }))}
                              value={formData.accessRequestType || ""}
                              name="accessRequestType"
                              onChange={handleInputChange}
                              placeholder={textTranslator(
                                "requestTypePlaceholder",
                                "MY_REQUESTS",
                              )}
                              allowCreate={false}
                              allowSearch={false}
                              className="max-sm:text-sm mt-4"
                            />
                          </motion.div>
                        </div>

                        {/* Access Issue Description */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 }}
                        >
                          <label
                            className={arClass(
                              `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                            )}
                          >
                            <Text>Access Issue Description</Text>
                          </label>
                          <textarea
                            name="accessIssueDescription"
                            value={formData.accessIssueDescription}
                            onChange={handleInputChange}
                            placeholder={textTranslator(
                              "descriptionPlaceholder",
                              "MY_REQUESTS",
                            )}
                            rows={3}
                            className={arClass(
                              `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                              }`,
                            )}
                          />
                        </motion.div>

                        {/* Systems Affected */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.55 }}
                        >
                          <label
                            className={arClass(
                              `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                            )}
                          >
                            <Text>Systems Affected</Text>
                          </label>
                          <div
                            className={arClass(
                              "grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4",
                            )}
                          >
                            {systemOptions.map((system) => {
                              const IconComponent = system.icon;
                              return (
                                <label
                                  key={system.value}
                                  className={arClass(
                                    `flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      formData.systemsAffected.includes(
                                        system.value,
                                      )
                                        ? darkMode
                                          ? "border-blue-500 bg-blue-900/20"
                                          : "border-blue-500 bg-blue-50"
                                        : darkMode
                                          ? "border-gray-700 hover:border-gray-600 bg-gray-700"
                                          : "border-gray-200 hover:border-gray-300 bg-gray-50"
                                    }`,
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.systemsAffected.includes(
                                      system.value,
                                    )}
                                    onChange={() =>
                                      handleSystemToggle(system.value)
                                    }
                                    className="rounded cursor-pointer w-4 h-4"
                                  />
                                  <IconComponent className="w-4 h-4" />
                                  <span
                                    className={
                                      darkMode
                                        ? "text-gray-300"
                                        : "text-gray-700"
                                    }
                                  >
                                    {system.name}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </motion.div>

                        {/* Requested Action */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.6 }}
                        >
                          <label
                            className={arClass(
                              `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                            )}
                          >
                            <Text>Requested Action</Text>
                          </label>
                          <textarea
                            name="requestedAction"
                            value={formData.requestedAction}
                            onChange={handleInputChange}
                            placeholder={textTranslator(
                              "descriptionPlaceholder",
                              "MY_REQUESTS",
                            )}
                            rows={3}
                            className={arClass(
                              `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                              }`,
                            )}
                          />
                        </motion.div>

                        {/* Permanent Deletion Checkbox */}
                        {formData.accessRequestType === "DELETION" && (
                          <motion.label
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.65 }}
                            className={arClass(
                              `flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer ${
                                darkMode
                                  ? "bg-red-900/20 border-red-700"
                                  : "bg-red-50 border-red-200"
                              }`,
                            )}
                          >
                            <input
                              type="checkbox"
                              name="permanentDeletion"
                              checked={formData.permanentDeletion}
                              onChange={handleInputChange}
                              className="rounded w-4 h-4 cursor-pointer"
                            />
                            <span
                              className={`font-medium flex gap-1 ${darkMode ? "text-red-400" : "text-red-700"}`}
                            >
                              <Info /> <Text>userAccessRequestNotice</Text>
                            </span>
                          </motion.label>
                        )}

                        {/* Data Handling */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.7 }}
                        >
                          <label
                            className={arClass(
                              `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                            )}
                          >
                            <Text>Data Handling</Text>
                          </label>
                          <textarea
                            name="dataHandling"
                            value={formData.dataHandling}
                            onChange={handleInputChange}
                            placeholder={textTranslator(
                              "descriptionPlaceholder",
                              "MY_REQUESTS",
                            )}
                            rows={3}
                            className={arClass(
                              `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                              }`,
                            )}
                          />
                        </motion.div>
                      </div>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className={arClass("flex gap-4 pt-4")}
                      >
                        <motion.button
                          type="submit"
                          disabled={loading}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={arClass(
                            `flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold transition-all ${
                              loading
                                ? "opacity-50 cursor-not-allowed"
                                : darkMode
                                  ? "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg shadow-blue-600/50"
                                  : "bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white shadow-lg shadow-blue-500/50"
                            }`,
                          )}
                        >
                          <Save className="w-5 h-5" />
                          <Text>
                            {loading ? "Creating Request..." : "Create Request"}
                          </Text>
                        </motion.button>
                      </motion.div>
                    </motion.div>
                  )}
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateUserAccessRequest;
